import * as THREE from 'three';
import React, { useMemo, useRef, useEffect } from 'react';
import { correctAxisData } from '../../utils';
import { compute3DMeshDataAndPosition, loadTexture } from '../../utils';
import * as constantsViewer from '../../constants';
import {
  createReactThreeGeometry,
  createReactThreeMaterial,
} from '../../reactThreeUtils';
import { getMaterialData, manageOnClickComponent } from '../../helpers';
import { useSpring, a } from 'react-spring/three';
import textureMirror from 'assets/images/textures/mirror2.jpg';
import textureAlu from 'assets/images/textures/alu-inox.jpg';
import textureWhiteBeech from 'assets/images/textures/whitebeech.jpg';
import textureMdfNatural from 'assets/images/textures/mdfnatural2.jpg';
import textureGlass from 'assets/images/textures/glass2.jpg';
import data from './data/pane';
import { onPointerDown, onPointerUp } from '../../helpers';
import { useDispatch } from 'react-redux';
import { applyPaintColor } from 'containers/PaintPanel/actions';

export function Pane({
  pane = null,
  cabinetId,
  shelfLevel,
  configuratorMode,
  rackLevel,
  pPosition = 'B',
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  const mesh = useRef();
  const dispatch = useDispatch();

  const baseData = useMemo(() => compute3DMeshDataAndPosition(data), [data]);

  const textures = useMemo(
    () => ({
      textureWhiteBeechLoaded: loadTexture(textureWhiteBeech),
      textureMdfNaturalLoaded: loadTexture(textureMdfNatural),
      textureMirrorLoaded: loadTexture(textureMirror),
      textureAluLoaded: loadTexture(textureAlu),
      textureGlassLoaded: loadTexture(textureGlass),
    }),
    [
      textureWhiteBeech,
      textureMdfNatural,
      textureMirror,
      textureAlu,
      textureGlass,
    ],
  );

  const materialData = getMaterialData(pane, textures);

  const [spring, set] = useSpring(() => ({
    position: getPosition(
      baseData.position,
      baseData.size,
      cabinetWidth,
      cabinetDepth,
      pPosition,
    ),
    scale: getScale(
      baseData.size,
      baseData.size,
      cabinetWidth,
      cabinetDepth,
      pPosition,
    ),
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    let self = mesh.current;
    if (self) {
      self.geometry.computeBoundingBox();
      let size = new THREE.Vector3();
      self.geometry.boundingBox.getSize(size);

      set({
        position: getPosition(
          baseData.position,
          baseData.size,
          cabinetWidth,
          cabinetDepth,
          pPosition,
        ),
        scale: getScale(
          baseData.size,
          size,
          cabinetWidth,
          cabinetDepth,
          pPosition,
        ),
      });
    }
  }, [cabinetWidth, cabinetDepth]);

  return (
    <a.mesh
      ref={mesh}
      receiveShadow
      castShadow
      onPointerDown={e => onPointerDown(e)}
      onPointerUp={e =>
        onPointerUp(e, e => {
          if (pPosition === 'UG' || pPosition === 'OG') return;

          manageOnClickComponent(
            e,
            unselected,
            pane,
            cabinetId,
            dispatch,
            configuratorMode,
            shelfLevel,
            rackLevel,
          );
        })
      }
      position={spring.position}
      scale={spring.scale}
      rotation={getRotation(pPosition)}
      renderOrder={getRenderOrder(pPosition)}
    >
      {createReactThreeGeometry(baseData)}
      {createReactThreeMaterial(materialData, unselected)}
    </a.mesh>
  );
}

function getRenderOrder(pPosition) {
  switch (pPosition) {
    case 'B':
    case 'R':
    case 'L':
      return constantsViewer.ZORDER_PANES_SIDES_BACK;
    case 'U' /* Under */:
      return constantsViewer.ZORDER_INNER_PLATE;

    case 'UG' /* Under glass */:
      return constantsViewer.ZORDER_INNER_PLATE_GLASS;

    case 'O' /* Over */:
      return constantsViewer.ZORDER_UPPER_PLATE;

    case 'OG' /* Over glass */:
      return constantsViewer.ZORDER_UPPER_PLATE_GLASS;

    default:
      return null;
  }
}

function getPosition(
  basePosition,
  size,
  cabinetWidth,
  cabinetDepth,
  pPosition,
) {
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;
  const paneSideDepth = cabinetDepth - 39;

  switch (pPosition) {
    case 'B':
      return [
        basePosition.x,
        basePosition.y,
        basePosition.z - cabinetDiffDepth / 2,
      ];

    case 'R':
      return [
        basePosition.x + cabinetWidth / 2 - size.z / 2 - 10,
        basePosition.y,
        basePosition.z + paneSideDepth / 2 - cabinetDiffDepth / 2 + 10,
      ];

    case 'L':
      return [
        basePosition.x - cabinetWidth / 2 + size.z / 2 + 5,
        basePosition.y,
        basePosition.z + paneSideDepth / 2 - cabinetDiffDepth / 2 + 10,
      ];

    case 'U' /* Under */:
      return [
        basePosition.x,
        basePosition.y - constantsViewer.MODEL_PANE_HEIGHT / 2 + 4,
        basePosition.z + paneSideDepth / 2 - cabinetDiffDepth / 2 + 10,
      ];

    case 'UG' /* Under glass */:
      return [
        basePosition.x,
        basePosition.y - constantsViewer.MODEL_PANE_HEIGHT / 2 + 5.5,
        basePosition.z + paneSideDepth / 2 - cabinetDiffDepth / 2 + 10,
      ];

    case 'O' /* Over */:
      return [
        basePosition.x,
        basePosition.y + constantsViewer.MODEL_PANE_HEIGHT / 2 + 34,
        basePosition.z + paneSideDepth / 2 - cabinetDiffDepth / 2 + 10,
      ];

    case 'OG' /* Over glass */:
      return [
        basePosition.x,
        basePosition.y + constantsViewer.MODEL_PANE_HEIGHT / 2 + 35.7,
        basePosition.z + paneSideDepth / 2 - cabinetDiffDepth / 2 + 10,
      ];

    default:
      return null;
  }
}

function getRotation(pPosition) {
  switch (pPosition) {
    case 'B':
      return [0, 0, 0];

    case 'R':
      return [0, -Math.PI / 2, 0];

    case 'L':
      return [0, Math.PI / 2, 0];

    case 'U' /* Under */:
    case 'UG' /* Under glass */:
    case 'O' /* Over */:
    case 'OG' /* Over glass */:
      return [Math.PI / 2, 0, 0];

    default:
      return null;
  }
}

function getScale(baseSize, size, cabinetWidth, cabinetDepth, pPosition) {
  let spaceWidthNoScale = constantsViewer.MODEL_CABINET_WIDTH - baseSize.x;
  switch (pPosition) {
    case 'B':
      return [(cabinetWidth - spaceWidthNoScale) / size.x, 1, 1];

    case 'R':
    case 'L':
      return [(cabinetDepth - spaceWidthNoScale) / size.x, 1, 1];

    case 'U' /* Under */:
    case 'UG' /* Under glass */:
    case 'O' /* Over */:
    case 'OG' /* Over glass */:
      return [
        (cabinetWidth - spaceWidthNoScale) / size.x,
        (cabinetDepth - spaceWidthNoScale) / size.y,
        1,
      ];

    default:
      return null;
  }
}
